import type { CeoPaymentApprovalRow } from '@shared/api';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';
import { formatDateRu, formatMoney, PAYMENT_CATEGORY_LABELS } from '../ceoFormat';
import {
  AmountColumn,
  ApprovedAmountText,
  BankDocText,
  CarryoverBadge,
  CarryoverBadgeText,
  CategoryText,
  CounterpartyText,
  DecisionActionsRow,
  DemoBadge,
  ExecutedText,
  InlineInputRowEnd,
  InitiatorText,
  MiniBtn,
  MutedDateText,
  PaidText,
  PurposeTd,
  RemainingBalanceText,
  RequestNumberRow,
  RequestNumberText,
  RequestedText,
  RightAlignTd,
  SmallInput,
  StickyRecipientTd,
  StickyTd,
} from './styles';
import type { DecideApprovalFn } from './types';

interface ApprovalRowProps {
  item: CeoPaymentApprovalRow;
  inlineAmount: number | undefined;
  onInlineAmountChange: (id: string, amount: number) => void;
  can_write: boolean;
  isDeciding: boolean;
  onDecide: DecideApprovalFn;
}

export function ApprovalRow({
  item,
  inlineAmount,
  onInlineAmountChange,
  can_write,
  isDeciding,
  onDecide,
}: ApprovalRowProps) {
  const requestedAmt = item.requested_amount || item.amount;
  const rawVal =
    inlineAmount ??
    (item.approved_amount && item.approved_amount > 0 ? item.approved_amount : item.amount);
  const currentVal = Math.max(
    0,
    Math.min(requestedAmt, Number.isFinite(rawVal) ? rawVal : 0)
  );
  const cutAmt = Math.max(0, requestedAmt - currentVal);
  const isExecutedOrPaid =
    item.decision === 'executed' || item.payment_execution_status === 'paid';

  return (
    <tr>
      <td>
        <RequestNumberRow>
          <RequestNumberText>{item.request_number}</RequestNumberText>
          {(item.id.startsWith('a1000001-') || item.id.startsWith('f1000001-')) && (
            <DemoBadge $tone="neutral">Демо-данные</DemoBadge>
          )}
        </RequestNumberRow>
        {item.request_number.includes('-ОСТ') && (
          <CarryoverBadgeText>
            ↩ Перенесённый остаток из реестра
          </CarryoverBadgeText>
        )}
        <MutedDateText>{formatDateRu(item.planned_pay_date)}</MutedDateText>
      </td>
      <td>
        <Badge
          $tone={
            item.priority === 'critical'
              ? 'danger'
              : item.priority === 'high'
                ? 'warning'
                : 'neutral'
          }
        >
          {item.priority === 'critical'
            ? 'Критичный'
            : item.priority === 'high'
              ? 'Высокий'
              : item.priority === 'normal'
                ? 'Плановый'
                : 'Низкий'}
        </Badge>
      </td>
      <StickyRecipientTd>
        <CounterpartyText>{item.counterparty_name}</CounterpartyText>
        <CategoryText>
          {item.bank_name ?? 'Основной р/с'} ·{' '}
          {PAYMENT_CATEGORY_LABELS[item.category] ?? item.category}
        </CategoryText>
      </StickyRecipientTd>
      <PurposeTd>
        <div>{item.purpose}</div>
        <InitiatorText>
          Инициатор: {item.initiator_name ?? '—'}
          {item.decision_comment ? ` · ${item.decision_comment}` : ''}
        </InitiatorText>
      </PurposeTd>
      <td>
        <Badge
          $tone={
            item.budget_status === 'within_limit'
              ? 'success'
              : item.budget_status === 'over_limit'
                ? 'warning'
                : 'danger'
          }
        >
          {item.budget_status === 'within_limit'
            ? 'В лимите БДДС'
            : item.budget_status === 'over_limit'
              ? 'Сверх лимита'
              : 'Вне бюджета'}
        </Badge>
      </td>

      {/* Запрошено / К акцепту */}
      <RightAlignTd>
        <AmountColumn>
          <RequestedText>
            Запрошено: <b>{formatMoney(requestedAmt)}</b>
          </RequestedText>

          {can_write &&
          item.decision !== 'approved' &&
          item.decision !== 'executed' &&
          !isExecutedOrPaid ? (
            <>
              <InlineInputRowEnd>
                <SmallInput
                  type="number"
                  min={0}
                  max={requestedAmt}
                  step="0.01"
                  value={currentVal}
                  onChange={(e) => {
                    const val = Number(e.target.value);
                    const clamped = Number.isNaN(val)
                      ? 0
                      : Math.max(0, Math.min(requestedAmt, val));
                    onInlineAmountChange(item.id, Math.round(clamped * 100) / 100);
                  }}
                />
                <MiniBtn
                  type="button"
                  onClick={() => {
                    const val = Math.round(requestedAmt * 1.0 * 100) / 100;
                    onInlineAmountChange(item.id, Math.max(0, Math.min(requestedAmt, val)));
                  }}
                >
                  100%
                </MiniBtn>
                <MiniBtn
                  type="button"
                  onClick={() => {
                    const val = Math.round(requestedAmt * 0.5 * 100) / 100;
                    onInlineAmountChange(item.id, Math.max(0, Math.min(requestedAmt, val)));
                  }}
                >
                  50%
                </MiniBtn>
                <MiniBtn
                  type="button"
                  onClick={() => {
                    const val = Math.round(requestedAmt * 0.3 * 100) / 100;
                    onInlineAmountChange(item.id, Math.max(0, Math.min(requestedAmt, val)));
                  }}
                >
                  30%
                </MiniBtn>
              </InlineInputRowEnd>

              {cutAmt > 0 && (
                <CarryoverBadge>
                  ↪ Срез {formatMoney(cutAmt)} переносится в след. реестр
                </CarryoverBadge>
              )}
            </>
          ) : (
            <ApprovedAmountText>
              {formatMoney(item.approved_amount || item.amount)}
              {item.cut_amount && item.cut_amount > 0 ? (
                <CarryoverBadge>
                  ↪ Срезано в след. реестр: {formatMoney(item.cut_amount)}
                </CarryoverBadge>
              ) : null}
            </ApprovedAmountText>
          )}
        </AmountColumn>
      </RightAlignTd>

      {/* Факт по выписке банка */}
      <RightAlignTd>
        <AmountColumn>
          <Badge
            $tone={
              item.payment_execution_status === 'paid'
                ? 'success'
                : item.payment_execution_status === 'partially_paid'
                  ? 'warning'
                  : 'neutral'
            }
          >
            {item.payment_execution_status === 'paid'
              ? 'Оплачено'
              : item.payment_execution_status === 'partially_paid'
                ? 'Частично'
                : 'Ожидает выписки'}
          </Badge>
          <PaidText>
            Оплачено: <b>{formatMoney(item.paid_amount || 0)}</b>
          </PaidText>
          {item.payment_execution_status !== 'paid' && (
            <RemainingBalanceText>
              Остаток:{' '}
              <b>
                {formatMoney(
                  Math.max(
                    0,
                    (item.approved_amount || item.amount) - (item.paid_amount || 0)
                  )
                )}
              </b>
            </RemainingBalanceText>
          )}
          {item.bank_doc_numbers && <BankDocText>{item.bank_doc_numbers}</BankDocText>}
        </AmountColumn>
      </RightAlignTd>

      <td>
        <Badge
          $tone={
            item.decision === 'approved' || item.decision === 'executed'
              ? 'success'
              : item.decision === 'rejected'
                ? 'danger'
                : item.decision === 'deferred'
                  ? 'neutral'
                  : 'warning'
          }
        >
          {item.decision === 'approved'
            ? 'Акцептовано'
            : item.decision === 'executed'
              ? 'Исполнено'
              : item.decision === 'deferred'
                ? 'Отложено'
                : item.decision === 'rejected'
                  ? 'Отклонено'
                  : 'Ожидает решения'}
        </Badge>
      </td>

      {can_write && (
        <StickyTd>
          {isExecutedOrPaid ? (
            <ExecutedText>
              {item.decision === 'executed' ? 'Исполнено банком' : 'Оплачено по выписке'}
            </ExecutedText>
          ) : (
            <DecisionActionsRow>
              {item.decision !== 'approved' && (
                <Button
                  $size="sm"
                  disabled={isDeciding}
                  onClick={() => {
                    const sanitizedApproved = Math.max(
                      0,
                      Math.min(requestedAmt, Number.isFinite(currentVal) ? currentVal : 0)
                    );
                    const sanitizedCut = Math.max(0, requestedAmt - sanitizedApproved);
                    const comment =
                      sanitizedCut > 0
                        ? `Акцептовано Генеральным директором на ${formatMoney(sanitizedApproved)}, срез ${formatMoney(sanitizedCut)} перенесён в след. реестр`
                        : 'Акцептовано Генеральным директором к оплате';
                    onDecide(item.id, 'approved', sanitizedApproved, comment);
                  }}
                >
                  Акцептовать
                </Button>
              )}
              {item.decision !== 'deferred' && (
                <Button
                  $size="sm"
                  $variant="secondary"
                  disabled={isDeciding}
                  onClick={() =>
                    onDecide(
                      item.id,
                      'deferred',
                      undefined,
                      'Отложено до следующего платёжного дня'
                    )
                  }
                >
                  Отложить
                </Button>
              )}
              {item.decision !== 'rejected' && (
                <Button
                  $size="sm"
                  $variant="ghost"
                  disabled={isDeciding}
                  onClick={() =>
                    onDecide(
                      item.id,
                      'rejected',
                      undefined,
                      'Отклонено Генеральным директором'
                    )
                  }
                >
                  Отклонить
                </Button>
              )}
            </DecisionActionsRow>
          )}
        </StickyTd>
      )}
    </tr>
  );
}
